import { format, parseISO } from 'date-fns';
import { Button } from '@predoc/cow';
import { ColumnDef } from '@tanstack/react-table';
import type { MutableRefObject } from 'react';
import { Link } from 'react-router';
import {
  ExistingPatient,
  PatientSearchProperties,
  ProfileFilterState,
} from '../../types';
import { SortableHeader } from '@/components';
import { ProfileFilterDropdown } from './ProfileFilterDropdown';
import { PatientProfileSelector } from './PatientProfileSelector';
import { PatientActionDropdown } from './PatientActionDropdown';
import { RequestStatusIcons } from '../common';

// Details Button Component
const DetailsButton = ({
  row,
}: {
  row: { getIsExpanded: () => boolean; toggleExpanded: () => void };
}) => {
  const isExpanded = row.getIsExpanded();

  const handleToggleExpansion = () => {
    row.toggleExpanded();
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleToggleExpansion}
      className={`h-6 text-xs ${
        isExpanded ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
      }`}
    >
      {isExpanded ? 'Close' : 'Details'}
    </Button>
  );
};

// Column definitions for DataTable
export const createColumns = (
  search: PatientSearchProperties,
  onProfileFilterChange: (profileFilter: ProfileFilterState) => void,
  onEditPatient: (patient: ExistingPatient) => void,
  profileFilterOpenRef?: MutableRefObject<boolean>,
): ColumnDef<ExistingPatient>[] => [
  {
    accessorKey: 'fullName',
    enableSorting: true,
    accessorFn: (row) => {
      const { firstName, middleName, lastName } = row;
      return `${firstName || ''} ${middleName || ''} ${lastName || ''}`.trim();
    },
    sortingFn: 'alphanumeric',
    header: ({ column }) => (
      <SortableHeader column={column}>Patient</SortableHeader>
    ),
    cell: ({ row }) => {
      const { id, firstName, middleName, lastName } = row.original;
      const fullName =
        `${firstName || ''} ${middleName || ''} ${lastName || ''}`.trim();
      return (
        <Link
          to={`/patients/${id}`}
          title={fullName}
          className="block min-w-0 truncate text-sm font-medium text-gray-900 hover:underline"
        >
          {firstName} {middleName} {lastName}
        </Link>
      );
    },
  },
  {
    accessorKey: 'dateOfBirth',
    size: 110,
    enableSorting: true,
    sortingFn: 'datetime',
    header: ({ column }) => (
      <SortableHeader column={column}>DOB</SortableHeader>
    ),
    cell: ({ row }) => (
      <div className="text-sm text-gray-900">
        {row.original.dateOfBirth
          ? format(parseISO(row.original.dateOfBirth), 'MM/dd/yyyy')
          : '--'}
      </div>
    ),
  },
  {
    accessorKey: 'organizationProfile',
    size: 250,
    header: () => (
      <div className="flex items-center gap-1">
        Profile
        <ProfileFilterDropdown
          profileFilter={search}
          onProfileFilterChange={onProfileFilterChange}
          openStateRef={profileFilterOpenRef}
        />
      </div>
    ),
    cell: ({ row }) => <PatientProfileSelector patient={row.original} />,
  },
  {
    accessorKey: 'statuses',
    size: 500,
    header: 'Statuses',
    cell: ({ row }) => {
      const patientRequestStatuses = row.original.organizationStatus || {};

      return <RequestStatusIcons statusCounts={patientRequestStatuses} />;
    },
  },
  {
    id: 'details',
    header: '',
    meta: { className: 'text-right' },
    cell: ({ row }) => <DetailsButton row={row} />,
  },
  {
    accessorKey: 'updatedAt',
    enableSorting: true,
    sortingFn: 'datetime',
    sortDescFirst: true,
    header: ({ column }) => (
      <SortableHeader column={column}>Last Modified</SortableHeader>
    ),
    cell: ({ row }) => (
      <div className="text-sm text-gray-900">
        {row.original.updatedAt
          ? new Date(row.original.updatedAt).toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
            })
          : 'N/A'}
      </div>
    ),
  },
  {
    id: 'actions',
    size: 56,
    header: '',
    cell: ({ row }) => (
      <PatientActionDropdown
        patient={row.original}
        onEditPatient={onEditPatient}
      />
    ),
  },
];
